import React, { memo } from "react";
import { Transaction } from "@/context/FinanceContext";
import { ColumnConfig } from "./ColumnVisibilitySelector";
import { MemoizedCell } from "./MemoizedCell";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

interface Category {
  id: string;
  name: string;
  color?: string;
}

interface Account {
  id: string;
  name: string;
  color?: string;
}

interface PaymentMethod {
  id: string;
  name: string;
}

interface CostCenter {
  id: string;
  name: string;
}

interface Supplier {
  id: string;
  name: string;
}

interface MemoizedTransactionRowProps {
  index: number;
  style: React.CSSProperties;
  transaction: Transaction;
  visibleColumns: ColumnConfig[];
  selected: Set<string>;
  categoryById: Map<string, Category>;
  accountById: Map<string, Account>;
  paymentMethodById: Map<string, PaymentMethod>;
  costCenterById: Map<string, CostCenter>;
  supplierById: Map<string, Supplier>;
  onSelectTransaction: (id: string, checked: boolean) => void;
  attachmentCount?: number;
  onOpenAttachments?: (transaction: Transaction) => void;
}

export const MemoizedTransactionRow = memo<MemoizedTransactionRowProps>(({
  index,
  style,
  transaction,
  visibleColumns,
  selected,
  categoryById,
  accountById,
  paymentMethodById,
  costCenterById,
  supplierById,
  onSelectTransaction,
  attachmentCount = 0,
  onOpenAttachments,
}) => {

  // Validação de segurança para prevenir erros de renderização
  if (!transaction || !transaction.id) {
    return (
      <div
        style={style}
        className="flex items-center border-b border-muted bg-destructive/10 text-destructive p-2"
      >
        <div className="text-sm">Erro: Dados da transação inválidos</div>
      </div>
    );
  }

  return (
    <div
      style={style}
      className={cn(
        "flex items-center border-b border-muted hover:bg-muted/30 transition-colors overflow-hidden relative",
        index % 2 === 0 ? "bg-background" : "bg-muted/10",
        selected.has(transaction.id) && "bg-blue-50"
      )}
    >
      {/* Barra lateral colorida por tipo */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-[3px]",
          transaction.type === "receita" && transaction.kind !== "transferencia" && "bg-emerald-500",
          transaction.type === "despesa" && transaction.kind !== "transferencia" && "bg-red-500",
          transaction.kind === "transferencia" && "bg-blue-500"
        )}
      />
      {/* Checkbox */}
      <div className="w-12 flex items-center justify-center border-r border-muted flex-shrink-0">
        <Checkbox
          checked={selected.has(transaction.id)}
          onCheckedChange={(checked) => onSelectTransaction(transaction.id, !!checked)}
        />
      </div>

      {/* Data columns */}
      {visibleColumns.map((column) => (
        <div
          key={column.key}
          className="border-r border-muted px-3 py-2 flex-shrink-0 overflow-hidden flex items-center justify-center"
          style={{ width: `${column.width}px` }}
        >
          <MemoizedCell
            transaction={transaction}
            column={column}
            categoryById={categoryById}
            accountById={accountById}
            paymentMethodById={paymentMethodById}
            costCenterById={costCenterById}
            supplierById={supplierById}
            attachmentCount={attachmentCount}
            onOpenAttachments={onOpenAttachments}
          />

        </div>
      ))}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for better performance - include ALL editable transaction fields
  return (
    prevProps.transaction.id === nextProps.transaction.id &&
    prevProps.transaction.description === nextProps.transaction.description &&
    prevProps.transaction.value === nextProps.transaction.value &&
    prevProps.transaction.type === nextProps.transaction.type &&
    prevProps.transaction.categoryId === nextProps.transaction.categoryId &&
    prevProps.transaction.accountId === nextProps.transaction.accountId &&
    prevProps.transaction.costCenterId === nextProps.transaction.costCenterId &&
    prevProps.transaction.paymentMethodId === nextProps.transaction.paymentMethodId &&
    prevProps.transaction.supplierId === nextProps.transaction.supplierId &&
    prevProps.transaction.issueDate === nextProps.transaction.issueDate &&
    prevProps.transaction.dueDate === nextProps.transaction.dueDate &&
    prevProps.transaction.paymentDate === nextProps.transaction.paymentDate &&
    prevProps.transaction.parcelMode === nextProps.transaction.parcelMode &&
    prevProps.transaction.parcels === nextProps.transaction.parcels &&
    prevProps.transaction.parcelIndex === nextProps.transaction.parcelIndex &&
    prevProps.transaction.issueDateMode === nextProps.transaction.issueDateMode &&
    prevProps.transaction.settled === nextProps.transaction.settled &&
    prevProps.transaction.reconciled === nextProps.transaction.reconciled &&
    prevProps.selected.has(prevProps.transaction.id) === nextProps.selected.has(nextProps.transaction.id) &&
    prevProps.visibleColumns === nextProps.visibleColumns &&
    prevProps.attachmentCount === nextProps.attachmentCount &&
    prevProps.index === nextProps.index

  );
});

MemoizedTransactionRow.displayName = "MemoizedTransactionRow";